import api, { Base, Tags } from 'ev-api/api';

import { CdxProxyEnvelope } from './module-types';

export type CdxSecretLastUpdate = {
  action: string;
  actor_email: string;
  at: string;
};

export type CdxSecretsIndex = {
  keys: string[];
  value_lengths: Record<string, number | null | undefined>;
  last_updates: Record<string, CdxSecretLastUpdate | null | undefined>;
};

export type CdxCustomerIdParam = {
  cdxCustomerId: number | string;
};

export type CreateCdxSecretParams = CdxCustomerIdParam & {
  key: string;
  value: string;
};

export type UpdateCdxSecretParams = CdxCustomerIdParam & {
  key: string;
  value: string;
};

export type DeleteCdxSecretParams = CdxCustomerIdParam & {
  key: string;
};

export type BatchCdxSecretsParams = CdxCustomerIdParam & {
  secrets: Record<string, string>;
};

const cdxSecretsApi = api.injectEndpoints({
  endpoints: builder => ({
    listCdxSecrets: builder.query<CdxSecretsIndex, CdxCustomerIdParam>({
      query: ({ cdxCustomerId }) => ({
        url: `${Base.V4}/cdx_proxy/config/customers/${cdxCustomerId}/secrets`,
      }),
      transformResponse: (response: CdxProxyEnvelope<CdxSecretsIndex>) =>
        response.data,
      providesTags: [Tags.CdxSecrets],
    }),

    createCdxSecret: builder.mutation<{ key: string }, CreateCdxSecretParams>({
      query: ({ cdxCustomerId, key, value }) => ({
        url: `${Base.V4}/cdx_proxy/config/customers/${cdxCustomerId}/secrets`,
        method: 'POST',
        body: { key, value },
      }),
      transformResponse: (response: CdxProxyEnvelope<{ key: string }>) =>
        response.data,
      invalidatesTags: [Tags.CdxSecrets, Tags.CdxModuleInstances],
    }),

    updateCdxSecret: builder.mutation<{ key: string }, UpdateCdxSecretParams>({
      query: ({ cdxCustomerId, key, value }) => ({
        url: `${Base.V4}/cdx_proxy/config/customers/${cdxCustomerId}/secrets/${encodeURIComponent(key)}`,
        method: 'PATCH',
        body: { value },
      }),
      transformResponse: (response: CdxProxyEnvelope<{ key: string }>) =>
        response.data,
      invalidatesTags: [Tags.CdxSecrets, Tags.CdxModuleInstances],
    }),

    deleteCdxSecret: builder.mutation<void, DeleteCdxSecretParams>({
      query: ({ cdxCustomerId, key }) => ({
        url: `${Base.V4}/cdx_proxy/config/customers/${cdxCustomerId}/secrets/${encodeURIComponent(key)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [Tags.CdxSecrets, Tags.CdxModuleInstances],
    }),

    batchCdxSecrets: builder.mutation<
      { keys: string[] },
      BatchCdxSecretsParams
    >({
      query: ({ cdxCustomerId, secrets }) => ({
        url: `${Base.V4}/cdx_proxy/config/customers/${cdxCustomerId}/secrets/batch`,
        method: 'PUT',
        body: { secrets },
      }),
      transformResponse: (response: CdxProxyEnvelope<{ keys: string[] }>) =>
        response.data,
      invalidatesTags: [Tags.CdxSecrets, Tags.CdxModuleInstances],
    }),
  }),
});

export const {
  useListCdxSecretsQuery,
  useCreateCdxSecretMutation,
  useUpdateCdxSecretMutation,
  useDeleteCdxSecretMutation,
  useBatchCdxSecretsMutation,
} = cdxSecretsApi;
